import React, { useState } from "react";
import axios from "axios";
import BASE_URL from "../../../assets/global/url";

const RecalculateAccountBalance = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // holds preview result until confirmed
  const [result, setResult] = useState(null); // holds final confirmed result
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    setError("");
    setPreview(null);
    setResult(null);

    if (!selectedFile) {
      setFile(null);
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      setError("Please select a CSV file.");
      setFile(null);
      return;
    }

    setFile(selectedFile);
  };

  const uploadFile = async (confirm) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("confirm", confirm ? "true" : "false");

    const response = await axios.post(
      `${BASE_URL}/migration/recalculate-account-balances`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    return response.data;
  };

  const handlePreview = async () => {
    if (!file) {
      setError("Please select a CSV file.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setResult(null);
      setPreview(null);

      const data = await uploadFile(false);
      setPreview(data);
    } catch (error) {
      console.error(error);
      setError(
        error.response?.data?.message ||
          "Failed to calculate account balances.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setSaving(true);
      setError("");

      const data = await uploadFile(true);
      setResult(data);
      setPreview(null);
    } catch (error) {
      console.error(error);
      setError(
        error.response?.data?.message || "Failed to save account balances.",
      );
    } finally {
      setSaving(false);
    }
  };

  const formatPeso = (value) =>
    `₱${Number(value).toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <div className="mt-2">
      <h4>Recalculate Account Balances</h4>

      <div className="mb-3">
        <label className="form-label">Upload Transaction CSV</label>
        <input
          type="file"
          className="form-control"
          accept=".csv"
          onChange={handleFileChange}
        />
      </div>

      {file && (
        <div className="mb-3">
          Selected file: <strong className="ms-2">{file.name}</strong>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {!preview && !result && (
        <button
          className="btn btn-primary"
          onClick={handlePreview}
          disabled={!file || loading}
        >
          {loading ? "Calculating..." : "Preview Recalculation"}
        </button>
      )}

      {preview && (
        <div className="alert alert-warning mt-4">
          <div>
            <strong>Preview — nothing has been saved yet.</strong>
          </div>
          <div>
            Transactions to Update: <strong>{preview.totalToUpdate}</strong>
          </div>
          <div>
            Calculated Ending Balance:{" "}
            <strong>{formatPeso(preview.endingBalance)}</strong>
          </div>

          <div className="mt-3">
            <button
              className="btn btn-success me-2"
              onClick={handleConfirm}
              disabled={saving}
            >
              {saving ? "Saving..." : "Confirm & Save to Database"}
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPreview(null)}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="alert alert-success mt-4">
          <div>
            <strong>Success! Balances saved to the database.</strong>
          </div>
          <div>
            Transactions Updated: <strong>{result.totalUpdated}</strong>
          </div>
          <div>
            Ending Balance: <strong>{formatPeso(result.endingBalance)}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecalculateAccountBalance;
